import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  Subject,
  combineLatest,
  startWith,
  map,
  filter,
  tap,
  Observable,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { ExportSettingsComponent } from '../export-settings/export-settings.component';
import { ToolbarButton } from '../../../models/toolbar.models';
import { ExportSchedule } from 'src/app/core/models/core.models';
import { FormMode } from 'src/app/core/models/form.models';
import { AppState } from 'src/app/reducers';
import {
  deleteExportSchedule,
  loadExportSchedules,
} from 'src/app/core/actions/object.actions';
import {
  selectExportSchedules,
  selectExportSchedulesLoading,
} from 'src/app/core/reducers';
import { ToastService } from '@services/toast.service';
import { LoggerService } from '@services/logger.service';
import { Actions, ofType } from '@ngrx/effects';
import { objectChanged } from 'src/app/core/actions/core.actions';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-schedules',
  templateUrl: './schedules.component.html',
  styleUrls: ['./schedules.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SchedulesComponent implements OnInit {
  private _exportSettings?: ExportSettingsComponent;

  @ViewChild('exportSettings', { static: false })
  set exportSettings(comp: ExportSettingsComponent | undefined) {
    this._exportSettings = comp;

    if (!comp) return;

    // success$
    comp.success$
      .pipe(
        filter((ok) => ok === true),
        tap(() => {
          this.cancel();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // error$
    comp.error$
      .pipe(
        filter((err) => !!err),
        tap((err) => this.toastService.showError(err)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  @Input() objectId!: string;
  @Input() objectClassId!: number;

  loading$: Observable<boolean>;
  formMode$ = new Subject<FormMode | null>();
  schedule$ = new Subject<ExportSchedule | null>();
  exportSchedules$: Observable<ExportSchedule[]>;

  protected logger = inject(LoggerService);
  private actions$ = inject(Actions);
  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.exportSchedules$ = this.store.pipe(select(selectExportSchedules));
    this.loading$ = this.store.pipe(select(selectExportSchedulesLoading));
  }

  readonly showBackButton$ = this.formMode$.pipe(
    startWith(null),
    map((mode) => mode !== null),
  );

  readonly buttons$ = this.formMode$.pipe(
    startWith(null),
    map((mode) => {
      this.logger.debug('Form mode:', mode);
      const t = this.translate.instant.bind(this.translate);
      const all: ToolbarButton[] = [
        {
          type: 'button',
          variant: 'secondary',
          label: t('btn.cancel'),
          action: () => this.cancel(),
          shouldDisplay: mode !== null,
        },
        {
          type: 'button',
          variant: 'primary',
          label: t('btn.save'),
          action: () => this.save(),
          shouldDisplay: mode !== null,
        },
        {
          type: 'button',
          variant: 'primary',
          label: t('btn.add'),
          leadingIcon: ['fas', 'plus'],
          action: () => this.create(),
          shouldDisplay: mode === null,
        },
      ];
      return all.filter((b) => b.shouldDisplay);
    }),
  );

  readonly title$ = combineLatest([
    this.formMode$.pipe(startWith(null)),
    this.schedule$.pipe(startWith(null)),
  ]).pipe(
    map(([mode, schedule]) => {
      const t = this.translate.instant.bind(this.translate);
      if (mode === FormMode.Edit && schedule) {
        return t('export-schedule.edit.title', { name: schedule.title });
      }
      if (mode === FormMode.Create) {
        return t('export-schedule.create.title');
      }
      return t('export-schedule.title.schedules');
    }),
  );

  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private toastService = inject(ToastService);

  ngOnInit() {
    if (!this.objectId || this.objectClassId == null) {
      throw new Error('Both `objectId` and `objectClassId` are required');
    }

    // load once
    this.store.dispatch(
      loadExportSchedules({
        objectId: this.objectId,
        objectClassId: this.objectClassId,
      }),
    );

    this.actions$
      .pipe(
        ofType(objectChanged),
        tap((action) => {
          if (!action?.object) return;

          this.store.dispatch(
            loadExportSchedules({
              objectId: action.object.id,
              objectClassId: action.object.object_class_id,
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  create() {
    this.formMode$.next(FormMode.Create);
    this.schedule$.next(null);
  }

  scheduleEdit(s: ExportSchedule) {
    this.formMode$.next(FormMode.Edit);
    this.schedule$.next(s);
  }

  cancel() {
    this.formMode$.next(null);
    this.schedule$.next(null);
  }

  save() {
    this._exportSettings.submit();
  }

  trackById(_: number, item: ExportSchedule) {
    return item.id;
  }

  sidebarBack() {
    this.formMode$.next(null);
    this.schedule$.next(null);
  }

  delete(schedule: ExportSchedule) {
    const title = this.translateService.instant(
      'schedules.title.delete-operation',
    );

    const message = this.translateService.instant(
      'schedules.message.are-you-sure-you-want-to-delete-this-schedule?',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(
            deleteExportSchedule({ exportScheduleId: schedule.id }),
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }
}
